/**
 * Booking form state, validation and the request it becomes. The vendor
 * specific part (an air waybill) is decided by what the quote says the vendor
 * needs, so a vendor that asks for nothing extra shows no extra fields.
 */
import type {
  BookingOptions,
  BookingParty,
  CreateLaneBookingRequest,
  NormalizedQuote,
} from '../../../apis/lanePlanner';

export interface PartyForm {
  company: string;
  contact: string;
  address: string;
  facilityHours: string;
}

export interface BookingFormValues {
  shipper: PartyForm;
  consignee: PartyForm;
  description: string;
  poReference: string;
  pickupNumber: string;
  handlingNotes: string;
  waybillPrefix: string;
  waybillNumber: string;
}

export type BookingErrors = Partial<Record<string, string>>;

const emptyParty = (address: string): PartyForm => ({
  company: '',
  contact: '',
  address,
  facilityHours: '',
});

/** Addresses start as the lane's origin and destination, as in the Figma. */
export const emptyBookingForm = (
  origin: string,
  destination: string
): BookingFormValues => ({
  shipper: emptyParty(origin),
  consignee: emptyParty(destination),
  description: '',
  poReference: '',
  pickupNumber: '',
  handlingNotes: '',
  waybillPrefix: '',
  waybillNumber: '',
});

/** Cargo One lists whether the airline assigns the waybill; false means the agent brings their own. */
export const needsOwnWaybill = (quote: NormalizedQuote): boolean => {
  const info = quote.details?.booking as
    { awbAssignment?: boolean | null } | undefined;
  return info?.awbAssignment === false;
};

/** IATA check digit: the 8th digit is the first seven modulo 7. */
export const validWaybillNumber = (value: string): boolean =>
  /^\d{8}$/.test(value) && Number(value.slice(0, 7)) % 7 === Number(value[7]);

const required = (value: string): boolean => value.trim().length > 0;

export const validateBooking = (
  values: BookingFormValues,
  quote: NormalizedQuote
): BookingErrors => {
  const errors: BookingErrors = {};
  (['shipper', 'consignee'] as const).forEach((party) => {
    if (!required(values[party].company)) {
      errors[`${party}.company`] = 'Enter the company name.';
    }
    if (!required(values[party].address)) {
      errors[`${party}.address`] = 'Enter the address.';
    }
  });
  if (!required(values.description)) {
    errors.description = 'Describe the commodity.';
  }
  if (needsOwnWaybill(quote)) {
    if (!/^\d{3}$/.test(values.waybillPrefix)) {
      errors.waybillPrefix = 'Three digits.';
    }
    if (!validWaybillNumber(values.waybillNumber)) {
      errors.waybillNumber = 'Eight digits with a valid check digit.';
    }
  }
  return errors;
};

const trimmed = (value: string): string | undefined =>
  value.trim() || undefined;

const toParty = (party: PartyForm): BookingParty => ({
  company: party.company.trim(),
  address: party.address.trim(),
  ...(trimmed(party.contact) && { contact: party.contact.trim() }),
  ...(trimmed(party.facilityHours) && {
    facility_hours: party.facilityHours.trim(),
  }),
});

export const toBookingRequest = (
  quote: NormalizedQuote,
  values: BookingFormValues
): CreateLaneBookingRequest => {
  const options: BookingOptions = needsOwnWaybill(quote)
    ? {
        waybill_prefix: values.waybillPrefix,
        waybill_number: values.waybillNumber,
      }
    : {};
  return {
    quote_id: quote.id,
    shipper: toParty(values.shipper),
    consignee: toParty(values.consignee),
    commodity: {
      description: values.description.trim(),
      ...(trimmed(values.poReference) && {
        po_reference: values.poReference.trim(),
      }),
      ...(trimmed(values.pickupNumber) && {
        pickup_number: values.pickupNumber.trim(),
      }),
      ...(trimmed(values.handlingNotes) && {
        handling_notes: values.handlingNotes.trim(),
      }),
    },
    ...(Object.keys(options).length > 0 && { options }),
  };
};
